import {
  File02Icon,
  CheckmarkSquare02Icon,
  UserCircle02Icon
} from "@hugeicons-pro/core-solid-rounded";
import { SquareLock01Icon } from "@hugeicons-pro/core-solid-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { memo, useMemo } from "react";
import { Link } from "react-router-dom";
import { LiteTextEditor } from "shared/app/Editor/LiteTextEditor";
import { Avatar } from "shared/components/Avatar";
import { cx } from "shared/utils";
import { formatCalendar } from "../helpers/format-calendar";
import { NotificationKind, NotificationType } from "../types";
import { AxiosError } from "axios";
import { assigneeApi } from "api/assignee";

interface NotificationCardProps {
  id: number;
  authorName: string;
  recipientName?: string;
  readAt: string | null;
  richText?: any;
  type: NotificationType;
  kind: NotificationKind;
  url: string;
  metadata: any;
  createAt: string;
}

const mentionTypeMap = {
  mentioned_in_application_notes: {
    title: "Mention in application notes",
    format: "for",
    icon: File02Icon
  },
  mentioned_in_tasks: {
    title: "Mention in a task:",
    format: "",
    icon: CheckmarkSquare02Icon
  },
  mentioned_in_task_notes: {
    title: "Mention in a task:",
    format: "",
    icon: CheckmarkSquare02Icon
  },
  task_assigned: {
    title: "Task assigned to you",
    format: "",
    icon: CheckmarkSquare02Icon
  },
  lead_assigned: {
    title: "Lead assigned to you",
    format: "",
    icon: UserCircle02Icon
  },
  application_assigned: {
    title: "Rental application assigned to you",
    format: "",
    icon: File02Icon
  }
};

interface NotificationContentProps {
  isLeadAssignedNotification: boolean;
  isAssignedTaskNotification: boolean;
  leadPrimaryEmail?: string;
  assignedTaskLocation: string;
  richTextHtml?: string;
}

const NotificationContent = memo((props: NotificationContentProps) => {
  const {
    isLeadAssignedNotification,
    isAssignedTaskNotification,
    leadPrimaryEmail,
    assignedTaskLocation,
    richTextHtml
  } = props;

  if (isLeadAssignedNotification)
    return (
      <p className="text-content-400 text-base">{leadPrimaryEmail || "-"}</p>
    );

  if (isAssignedTaskNotification)
    return (
      <p className="text-content-400 text-base">
        {assignedTaskLocation || "-"}
      </p>
    );

  return (
    <LiteTextEditor
      id="notification-card-rich-text"
      editable={false}
      initialValue={richTextHtml}
      containerClassName="text-content-400 text-base"
    />
  );
});
NotificationContent.displayName = "NotificationContent";

export const NotificationCard = (props: NotificationCardProps) => {
  const {
    id,
    authorName,
    recipientName,
    readAt,
    richText,
    type,
    kind,
    url,
    metadata,
    createAt
  } = props;
  const queryClient = useQueryClient();
  const assignmentMeta = useMemo(() => {
    const isAssignedTaskNotification =
      type === NotificationType.AssignedTask ||
      (kind === NotificationKind.Assigned && Boolean(metadata.task_id));
    const isLeadAssignedNotification =
      type === NotificationType.LeadAssigned ||
      (kind === NotificationKind.Assigned && Boolean(metadata.lead_id));
    const isApplicationAssignedNotification =
      type === NotificationType.ApplicationAssigned ||
      (kind === NotificationKind.Assigned && Boolean(metadata.application_id));
    const isAssignedNotification =
      isAssignedTaskNotification ||
      isLeadAssignedNotification ||
      isApplicationAssignedNotification;

    let resolvedType = type;
    if (isAssignedTaskNotification)
      resolvedType = NotificationType.AssignedTask;
    else if (isLeadAssignedNotification)
      resolvedType = NotificationType.LeadAssigned;
    else if (isApplicationAssignedNotification)
      resolvedType = NotificationType.ApplicationAssigned;

    let assignedTitle = "";
    if (isLeadAssignedNotification) assignedTitle = "Lead assigned to you";
    else if (isAssignedTaskNotification) assignedTitle = "Task assigned to you";
    else if (isApplicationAssignedNotification)
      assignedTitle = "Rental application assigned to you";

    return {
      isAssignedTaskNotification,
      isLeadAssignedNotification,
      isApplicationAssignedNotification,
      isAssignedNotification,
      resolvedType,
      assignedTitle
    };
  }, [type, kind, metadata.task_id, metadata.lead_id, metadata.application_id]);
  const {
    isAssignedTaskNotification,
    isLeadAssignedNotification,
    isApplicationAssignedNotification,
    isAssignedNotification,
    resolvedType,
    assignedTitle
  } = assignmentMeta;

  const processedUrl = useMemo(() => {
    if (
      [NotificationType.TaskMention, NotificationType.TaskNoteMention].includes(
        resolvedType
      ) &&
      metadata.task_id
    )
      return `/tasks?task_id=${metadata.task_id}`;
    if (isAssignedTaskNotification && metadata.task_id)
      return `/tasks?task_id=${metadata.task_id}`;
    if (isLeadAssignedNotification && metadata.lead_id)
      return `/leads/${metadata.lead_id}`;
    if (isApplicationAssignedNotification && metadata.application_id)
      return `/applications/${metadata.application_id}`;

    return url;
  }, [
    resolvedType,
    isAssignedTaskNotification,
    isLeadAssignedNotification,
    isApplicationAssignedNotification,
    url,
    metadata
  ]);
  const { mutate: markAsRead } = useMutation({
    mutationKey: ["NOTIFICATIONS", "MARK_AS_READ"],
    mutationFn: (ids: number[]) =>
      instance.post("/portal/v1/notifications/mark_as_read", { ids }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["NOTIFICATIONS"] });
      queryClient.invalidateQueries({
        queryKey: ["UNREAD_NOTIFICATIONS_COUNT"]
      });
      queryClient.invalidateQueries({
        queryKey: ["TOTAL_UNREAD_NOTIFICATIONS"]
      });
      queryClient.invalidateQueries({ queryKey: ["NEWEST_NOTIFICATIONS"] });
    }
  });

  const handleClick = () => {
    if (!readAt) markAsRead([id]);
  };

  const { data: application, isLoading: isApplicationLoading } = useQuery({
    queryKey: ["application", metadata.application_id],
    queryFn: () =>
      instance(`/portal/v1/applications/${metadata.application_id}`).then(
        ({ data }) => data
      ),
    enabled: Boolean(metadata.application_id)
  });

  const {
    data: task,
    isLoading: isTaskLoading,
    error: taskError
  } = useQuery({
    queryKey: ["task", metadata.task_id],
    queryFn: () =>
      instance(`/portal/v1/tasks/${metadata.task_id}`).then(({ data }) => data),
    enabled: Boolean(metadata.task_id),
    retry: false
  });

  const {
    data: lead,
    isLoading: isLeadLoading,
    error: leadError
  } = useQuery({
    queryKey: ["LEAD", metadata.lead_id],
    queryFn: () =>
      instance(`/portal/v1/leads/${metadata.lead_id}`).then(({ data }) => data),
    enabled: Boolean(metadata.lead_id && isLeadAssignedNotification),
    retry: false
  });

  const { data: assignees } = useQuery({
    queryKey: ["ASSIGNEES"],
    queryFn: () => assigneeApi.getAssignees(),
    enabled:
      (isLeadAssignedNotification || isApplicationAssignedNotification) &&
      Boolean(metadata.assigned_by_user_id)
  });

  const leadName = useMemo(() => {
    if (!lead) return "";
    return [lead.first_name, lead.last_name].filter(Boolean).join(" ").trim();
  }, [lead]);

  const applicationName = useMemo(() => {
    if (!application) return "";
    return application.applicants
      .map((applicant: any) =>
        [applicant.first_name, applicant.last_name]
          .filter(Boolean)
          .join(" ")
          .trim()
      )
      .filter(Boolean)
      .join(", ");
  }, [application]);

  const assignedByName = useMemo(() => {
    if (!isLeadAssignedNotification && !isApplicationAssignedNotification)
      return "";

    if (metadata.assigned_by_name) return metadata.assigned_by_name;

    const assignedById = metadata.assigned_by_user_id;

    if (isLeadAssignedNotification && assignedById && assignees?.length) {
      const assignedByUser = assignees.find((user) => user.id === assignedById);
      if (assignedByUser?.name) return assignedByUser.name;
    }

    if (
      isApplicationAssignedNotification &&
      assignedById &&
      assignees?.length
    ) {
      const assignedByUser = assignees.find(
        (user: any) => user.id === assignedById
      );
      if (assignedByUser?.name) return assignedByUser.name;
    }

    return authorName || "System";
  }, [
    isLeadAssignedNotification,
    isApplicationAssignedNotification,
    metadata.assigned_by_name,
    metadata.assigned_by_user_id,
    assignees,
    authorName
  ]);

  const assignedTaskByText = useMemo(() => {
    if (!isAssignedTaskNotification) return "";
    return metadata.created_by_name || authorName || "System";
  }, [isAssignedTaskNotification, metadata.created_by_name, authorName]);

  const assignedTaskLocation = useMemo(() => {
    if (!isAssignedTaskNotification) return "";

    const propertyName =
      metadata.property_name || task?.subject?.property?.name;
    const unitNumber = metadata.unit_number;

    if (propertyName && unitNumber)
      return `${propertyName} - Unit ${unitNumber}`;
    return propertyName || unitNumber || "";
  }, [
    isAssignedTaskNotification,
    metadata.property_name,
    metadata.unit_number,
    task
  ]);

  const assignedSubjectName = useMemo(() => {
    if (isLeadAssignedNotification) return leadName || "-";
    if (isApplicationAssignedNotification) return applicationName || "-";

    if (!isAssignedTaskNotification) return "";

    return metadata.task_title || task?.title?.document_text || "-";
  }, [
    isLeadAssignedNotification,
    isApplicationAssignedNotification,
    isAssignedTaskNotification,
    leadName,
    applicationName,
    task,
    metadata.task_title
  ]);

  const assignedByDisplay = useMemo(() => {
    if (isLeadAssignedNotification) return assignedByName || "System";
    if (isApplicationAssignedNotification) return assignedByName || "System";
    if (isAssignedTaskNotification) return assignedTaskByText || "System";
    return "";
  }, [
    isLeadAssignedNotification,
    isApplicationAssignedNotification,
    isAssignedTaskNotification,
    assignedByName,
    assignedTaskByText
  ]);

  const assignedPropertyName = useMemo(() => {
    if (isLeadAssignedNotification)
      return metadata.property_name || lead?.property?.name || "";
    if (isApplicationAssignedNotification)
      return metadata.property_name || application?.property?.name || "";
    if (isAssignedTaskNotification)
      return metadata.property_name || task?.subject?.property?.name || "";
    return "";
  }, [
    isLeadAssignedNotification,
    isApplicationAssignedNotification,
    isAssignedTaskNotification,
    metadata.property_name,
    lead,
    application,
    task
  ]);

  const assignedUnitName = useMemo(() => {
    if (isLeadAssignedNotification)
      return metadata.unit_name || lead?.unit?.title || "";
    if (isApplicationAssignedNotification)
      return metadata.unit_name || application?.unit?.title || "";
    if (isAssignedTaskNotification)
      return metadata.unit_number || task?.subject?.unit?.title || "";
    return "";
  }, [
    isLeadAssignedNotification,
    isApplicationAssignedNotification,
    isAssignedTaskNotification,
    metadata.unit_name,
    metadata.unit_number,
    lead,
    application,
    task
  ]);

  const subjectName = useMemo(() => {
    if (resolvedType === NotificationType.ApplicationNotesMention) {
      if (!application)
        return "application in a property you don’t have access to";

      return (
        mentionTypeMap[resolvedType].format +
        " " +
        application.applicants
          .map((applicant) => applicant.first_name?.trim())
          .join(", ")
      );
    }
    if (
      [NotificationType.TaskMention, NotificationType.TaskNoteMention].includes(
        resolvedType
      )
    )
      return task?.title.document_text;
    if (isAssignedTaskNotification)
      return assignedTaskByText ? `By: ${assignedTaskByText}` : "";
    if (isLeadAssignedNotification)
      return assignedByName ? `by ${assignedByName}` : "";
    if (isApplicationAssignedNotification)
      return assignedByName ? `by ${assignedByName}` : "";
    return "";
  }, [
    application,
    task,
    resolvedType,
    isAssignedTaskNotification,
    isLeadAssignedNotification,
    isApplicationAssignedNotification,
    assignedByName,
    assignedTaskByText
  ]);
  const isTaskMention = useMemo(
    () =>
      [NotificationType.TaskMention, NotificationType.TaskNoteMention].includes(
        resolvedType
      ),
    [resolvedType]
  );
  const hasAccess = useMemo(() => {
    if (resolvedType === NotificationType.ApplicationNotesMention)
      return Boolean(application);
    if (isTaskMention) return Boolean(task);
    if (isAssignedTaskNotification)
      return metadata.task_id ? Boolean(task) : true;
    if (isLeadAssignedNotification) return Boolean(lead);
    if (isApplicationAssignedNotification) return Boolean(application);
    return true;
  }, [
    application,
    task,
    lead,
    resolvedType,
    isTaskMention,
    isAssignedTaskNotification,
    isLeadAssignedNotification,
    isApplicationAssignedNotification,
    metadata.task_id
  ]);

  const forbiddenState = useMemo(() => {
    if (hasAccess) return undefined;

    const isTaskDeleted =
      taskError instanceof AxiosError && taskError.response?.status === 404;

    if (isTaskMention && isTaskDeleted)
      return {
        message: "Not found",
        subtitle: "The task you were mentioned in has been deleted."
      };

    const isLeadDeleted =
      leadError instanceof AxiosError && leadError.response?.status === 404;

    if (isLeadAssignedNotification && isLeadDeleted)
      return {
        message: "Not found",
        subtitle: "This lead has been deleted."
      };

    return { message: "You don't have permission to access property" };
  }, [
    hasAccess,
    isTaskMention,
    taskError,
    isLeadAssignedNotification,
    leadError
  ]);

  const notificationDisplayName = useMemo(() => {
    if (isAssignedTaskNotification)
      return metadata.task_title || task?.title?.document_text || "Task";
    if (isLeadAssignedNotification && leadName) return leadName;
    if (isApplicationAssignedNotification && applicationName)
      return applicationName;
    return authorName;
  }, [
    isAssignedTaskNotification,
    metadata.task_title,
    task,
    isLeadAssignedNotification,
    isApplicationAssignedNotification,
    leadName,
    applicationName,
    authorName
  ]);

  if (isApplicationLoading || isTaskLoading || isLeadLoading) return null;
  const formattedCreateAt = formatCalendar(createAt);

  if (isAssignedNotification)
    return (
      <Link
        to={hasAccess ? processedUrl : "/forbidden"}
        state={forbiddenState}
        onClick={handleClick}
        className="p-6 bg-white hover:bg-alpha-40 cursor-pointer flex items-center w-full"
      >
        <div className="w-full flex justify-between gap-6">
          <div className="flex gap-4 items-center min-w-0">
            <span
              className={cx(
                "size-3 rounded-full shrink-0",
                Boolean(readAt) ? "bg-alpha-40" : "bg-primary-500"
              )}
            />
            <Avatar
              name={recipientName || authorName}
              size={44}
              className="font-bold shrink-0"
            />
            <div className="min-w-0">
              <p className="text-base font-semibold text-content-500 truncate">
                {assignedTitle}:{" "}
                <span className="font-medium text-content-300">
                  {assignedSubjectName}
                </span>
              </p>
              <p className="text-sm text-content-300 truncate">
                <span className="underline underline-offset-2">
                  by {assignedByDisplay}
                </span>
                {assignedPropertyName ? ` • ${assignedPropertyName}` : ""}
                {assignedUnitName ? ` • ${assignedUnitName}` : ""}
              </p>
            </div>
          </div>

          <p className="text-content-300 font-medium text-sm shrink-0">
            {formattedCreateAt}
          </p>
        </div>
      </Link>
    );

  return (
    <Link
      to={hasAccess ? processedUrl : "/forbidden"}
      state={forbiddenState}
      onClick={handleClick}
      className="p-6 bg-white hover:bg-alpha-40 cursor-pointer flex items-center w-full"
    >
      <div className="space-y-4 w-full">
        <div className="flex justify-between items-center w-full">
          {mentionTypeMap[resolvedType] ? (
            <div className="flex gap-2.5 items-center">
              <HugeiconsIcon
                icon={mentionTypeMap[resolvedType].icon}
                size={16}
                className="text-content-200"
              />
              <p className="text-content-300 font-medium flex items-center text-sm">
                {mentionTypeMap[resolvedType].title}{" "}
                {hasAccess ? (
                  <p className="ml-1">{subjectName}</p>
                ) : (
                  <>
                    {mentionTypeMap[resolvedType].format}
                    <HugeiconsIcon
                      icon={SquareLock01Icon}
                      className="size-4 text-content-100 mx-1"
                    />
                    <span className="text-content-100">{subjectName}</span>
                  </>
                )}
              </p>
            </div>
          ) : null}

          <p className="text-content-300 font-medium text-sm">
            {formattedCreateAt}
          </p>
        </div>

        <div className="flex gap-6 items-center">
          <span
            className={cx(
              "size-3 rounded-full shrink-0",
              Boolean(readAt) ? "bg-alpha-40" : "bg-primary-500"
            )}
          />
          {isLeadAssignedNotification || isAssignedTaskNotification ? null : (
            <Avatar name={authorName} size={48} className="font-bold" />
          )}
          <div className="space-y-1">
            <p className="text-base font-bold">{notificationDisplayName}</p>
            <NotificationContent
              isLeadAssignedNotification={isLeadAssignedNotification}
              isAssignedTaskNotification={isAssignedTaskNotification}
              leadPrimaryEmail={lead?.primary_email}
              assignedTaskLocation={assignedTaskLocation}
              richTextHtml={richText?.document_html}
            />
          </div>
        </div>
      </div>
    </Link>
  );
};
